import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useWebSocket from 'react-use-websocket';

import { useFetchChatSessionByIdQuery } from "./chatSessionApiSlice";
import { useCreateChatLogMutation, ws_url } from "../chat_logs/chatLogApiSlice";
import { withWsToken } from "../../app/api/apiSlice";
import { selectCurrentUser } from "../auth/authSlice";
import { useCreateEvaluationSessionMutation } from "../evaluation_sessions/evaluationSessionApiSlice"
import { useGetEvaluationCriteriaGroupsQuery } from "../evaluation_criteria_group/evaluationCriteriaGroupApiSlice"
import { updateBotResponse, updateModelCompletion, setHintText, resetBotResponse } from "./botResponseSlice"

import {
  Loader,
  Icon,
  Container,
  Button,
  Divider,
  Message,
  Header,
  Modal,
  Popup,
  Table,
  Dropdown
} from "semantic-ui-react";
import { PAGE_SIZE, formatDate, canEvaluate } from "../../constants";
import CustomSnackbar from "../../components/CustomSnackbar";

const ModelOptions = [
  { key: 'OpenAI', text: 'OpenAI', value: 'OPENAI' },
  { key: 'Custom model', text: 'Custom model', value: 'v1-3640-gptq' },
]

const ChatSessionPerform = (props, ref) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scrollRef = useRef();
  const inputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const chatsessionId = queryParams.get("chatsession_id");

  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const botResponse = useSelector((state) => state.botResponse.botResponse);
  const modelCompletion = useSelector((state) => state.botResponse.modelCompletion);
  const hintText = useSelector((state) => state.botResponse.hintText);
  const currentUser = useSelector(selectCurrentUser);

  const [seen, setSeen] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(false);
  const [isDialogueFinished, setIsDialogueFinished] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const model = ModelOptions[0].value;

  // Getting ChatSession
  const { data: chatSession, isLoading, isError, error, refetch } = useFetchChatSessionByIdQuery(chatsessionId);

  const isOwner = chatSession && currentUser ? chatSession.trainee.id === currentUser.id : false;

  useEffect(() => {
    if (chatSession && currentUser) {
      if (chatSession.trainee.id !== currentUser.id) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }
  }, [chatSession, currentUser]);

  // Getting Evaluation Criteria Groups
  const { data: criteria_groups, isLoading: isLoading_cg } = useGetEvaluationCriteriaGroupsQuery();

  // Getting the function for creating chatlog
  const [addChatLog, { data: addedChatLog, isLoading: isAdding }] = useCreateChatLogMutation();

  // Getting web socket connection. The url is a stable getter (memoized on the
  // session id) — react-use-websocket keys its connect effect on the url arg, so
  // an inline arrow would reconnect on every render (an infinite open/close
  // storm). withWsToken reads the token from localStorage at call time, so
  // reconnects still pick up a refreshed token.
  const getSocketUrl = useCallback(
    () => withWsToken(ws_url.replace("{session_id}", chatsessionId)),
    [chatsessionId]
  );
  const { lastMessage } = useWebSocket(getSocketUrl, {
    shouldReconnect: (closeEvent) => true,
  });

  // Getting messages
  useEffect(() => {
    if (chatSession){
      setMessages(chatSession.chatlog_set);
    }
  }, [chatSession, navigate]);

  // Handling stream bot response
  useEffect(() => {
    if (lastMessage) {
      dispatch(updateModelCompletion(lastMessage.data));
      if (model === ModelOptions[0].value) {
        if (!seen && /"response" *: *"/.test(modelCompletion)) {
          setSeen(true);
          setIsFirstMessage(true);
        } else if (isFirstMessage && !(/"response" *: *".*",/).test(modelCompletion)) {
          dispatch(updateBotResponse(lastMessage.data.replace('",', '')));
        } else if (isFirstMessage) {
          setIsFirstMessage(false);
        }
      } else {
        dispatch(updateBotResponse(lastMessage.data));
      }
    }
  }, [lastMessage]);

  // Handling re-fetch after adding chatlog
  useEffect(() => {
    if (addedChatLog) {
      refetch();
      dispatch(resetBotResponse());
      if (addedChatLog.is_dialogue_finished) {
        setIsDialogueFinished(true);
      }
      setHintText(addedChatLog.hint);
    }
  }, [addedChatLog, botResponse, dispatch, refetch])

  // Handling click the send-button
  const handleClickSend = async (input_text) => {
    if (!isOwner) {
      setSnackbarVisible(true);
      setTimeout(() => setSnackbarVisible(false), 3000);
      return;
    }

    setSeen(false);
    setInputText("");

    setMessages(messages.concat([
      {
        id: "user_input",
        utterance: input_text,
        speaker: "H",
      }
    ]));

    addChatLog({
      session: chatsessionId,
      utterance: input_text,
      is_practice: 0,
      stream: true,
      model: model,
    });
  };

  // Handling click confirm button
  const [createEvaluationSession, { isSuccess: isSuccess_eval, isLoading: isCreating_eval }] =
    useCreateEvaluationSessionMutation();
  // Same rule as the sessions list: one evaluation per chat session. `messages` mirrors
  // chatSession.chatlog_set, so it stands in for chatlog_count.
  // `isCreating_eval` closes the double-submit window: the modal confirms, then the page
  // navigates away on success, and in between the cached count is still 0.
  const canEval =
    !isCreating_eval &&
    canEvaluate({
      chatlog_count: messages.length,
      evaluation_session_count: chatSession?.evaluation_session_count,
    });

  const handleClickConfirm = () =>{
    createEvaluationSession({
      chat_session: chatsessionId,
      criteria_group: criteria_groups.results[0].id
    });
  };

  const handleOpenConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmEvaluation = () => {
    if (criteria_groups?.results?.length > 0) {
      createEvaluationSession({
        chat_session: chatsessionId,
        criteria_group: criteria_groups.results[0].id
      });
      setConfirmModalOpen(false);
    }
  };
  useEffect(() => {
    if (isSuccess_eval) {
      navigate(`/evaluationsessionslist?page=1&page_size=${PAGE_SIZE}`);
    }},
    [isSuccess_eval, navigate]
  );

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, botResponse]);

  // Loading
  if (isLoading || isLoading_cg) {
    return <Loader active />;
  }

  if (isError) return null;

  // Content
  let content;
  content = (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 50px)', backgroundColor: '#f9fafb' }}>
      {/* Header Section */}
      <div style={{ 
        padding: '16px 0', 
        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0,0,0,0.05)', 
        flexShrink: 0,
        zIndex: 10
      }}>
        <Container>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                flexShrink: 0, // stays a circle when the text beside it wraps
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#F2F2F7', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Icon name="comments" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
              </div>
              <div>
                <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>채팅 세션 진행</h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>고객 페르소나와 대화를 진행하세요</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {isMobile ? (
                <Dropdown
                  trigger={
                    <Button icon basic circular>
                      <Icon name="ellipsis horizontal" />
                    </Button>
                  }
                  pointing="top right"
                  icon={null}
                >
                  <Dropdown.Menu>
                    <Dropdown.Item
                      as={Link}
                      to={`/audio-orb-openai?session_id=${chatsessionId}`}
                    >
                      <Icon name="microphone" />
                      <span className="text">라이브 모드로 이어하기</span>
                    </Dropdown.Item>
                    {chatSession?.evaluation_session_count > 0 ? (
                      <Dropdown.Item
                        as={Link}
                        to={`/chatsessions/${chatsessionId}`}
                      >
                        <Icon name="list" />
                        <span className="text">평가 보기</span>
                      </Dropdown.Item>
                    ) : (
                      <Dropdown.Item disabled>
                        <Icon name="list" />
                        <span className="text">평가 보기 (없음)</span>
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      disabled={!canEval}
                      onClick={() => {
                        if (canEval) handleOpenConfirmModal();
                      }}
                    >
                      <Icon name="clipboard check" />
                      <span className="text">
                        {messages.length <= 6
                          ? '평가 실행 (대화 부족)'
                          : chatSession?.evaluation_session_count > 0
                          ? '평가 실행 (이미 완료)'
                          : '평가 실행'}
                      </span>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <>
                  <Button
                    as={Link}
                    to={`/audio-orb-openai?session_id=${chatsessionId}`}
                    size="small"
                    basic
                    icon
                    labelPosition="left"
                    color="violet"
                  >
                    <Icon name="microphone" />
                    라이브 모드로 이어하기
                  </Button>
                  {chatSession?.evaluation_session_count > 0 ? (
                    <Button
                      as={Link}
                      to={`/chatsessions/${chatsessionId}`}
                      size="small"
                      basic
                      icon
                      labelPosition="left"
                    >
                      <Icon name="list" />
                      평가 보기
                    </Button>
                  ) : (
                    <Popup
                      content="평가 세션이 없습니다."
                      trigger={
                        <span>
                          <Button
                            size="small"
                            basic
                            icon
                            labelPosition="left"
                            disabled
                          >
                            <Icon name="list" />
                            평가 보기
                          </Button>
                        </span>
                      }
                    />
                  )}
                  <Popup
                    content={
                      messages.length <= 6
                        ? '평가를 위해 최소 3턴 이상 필요'
                        : chatSession?.evaluation_session_count > 0
                        ? '이미 평가가 실행된 세션입니다'
                        : '평가 실행'
                    }
                    trigger={
                      <Button
                        size="small"
                        basic
                        icon
                        labelPosition="left"
                        positive={canEval}
                        disabled={!canEval}
                        onClick={() => {
                          if (canEval) handleOpenConfirmModal();
                        }}
                      >
                        <Icon name="clipboard check" />
                        평가 실행
                      </Button>
                    }
                  />
                </>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Chat Area */}
      <div 
        ref={scrollRef} 
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '20px 0',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Container>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {messages.map((message, index) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.speaker === "H" ? 'flex-end' : 'flex-start',
                  padding: '0 4px'
                }}
              >
                <div
                  style={{
                    maxWidth: '75%',
                    padding: '10px 16px',
                    borderRadius: '18px',
                    fontSize: '16px',
                    lineHeight: '1.5',
                    backgroundColor: message.speaker === "H" ? '#007AFF' : '#E9E9EB',
                    color: message.speaker === "H" ? 'white' : 'black',
                    borderBottomRightRadius: message.speaker === "H" ? '4px' : '18px',
                    borderBottomLeftRadius: message.speaker === "H" ? '18px' : '4px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    wordBreak: 'break-word'
                  }}
                >
                  {message.utterance}
                </div>
              </div>
            ))}

            {botResponse !== "" &&
              <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 4px' }}>
                <div style={{
                  maxWidth: '75%',
                  padding: '10px 16px',
                  borderRadius: '18px',
                  fontSize: '16px',
                  lineHeight: '1.5',
                  backgroundColor: '#E9E9EB',
                  color: 'black',
                  borderBottomLeftRadius: '4px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  wordBreak: 'break-word'
                }}>
                  {botResponse}
                </div>
              </div>
            }
          </div>

          {isDialogueFinished && (
            <div style={{ margin: '40px 0', textAlign: 'center' }}>
              <div style={{ 
                display: 'inline-block', 
                padding: '8px 16px', 
                backgroundColor: '#F2F2F7', 
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#86868b'
              }}>
                대화가 종료되었습니다
              </div>
              <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <Button 
                  size="small" 
                  onClick={() => { setIsDialogueFinished(false); }}
                  style={{ borderRadius: '20px', backgroundColor: '#E5E5EA', color: 'black' }}
                >
                  계속하기
                </Button>
                <Button 
                  size="small" 
                  primary 
                  onClick={() => { handleClickConfirm(); }}
                  style={{ borderRadius: '20px', backgroundColor: '#007AFF' }}
                >
                  평가 생성
                </Button>
              </div>
            </div>
          )}

          {hintText && (
            <Message info>
              <Message.Header>Hint</Message.Header>
              <p>{hintText}</p>
            </Message>
          )}
        </Container>
      </div>

      {/* Input Area */}
      <div style={{ 
        padding: '20px', 
        backgroundColor: 'white', 
        borderTop: '1px solid rgba(0,0,0,0.05)',
        flexShrink: 0
      }}>
        <Container>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && inputText.trim() !== "") {
                  handleClickSend(inputText);
                }
              }}
              placeholder="메시지를 입력하세요..."
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: '24px',
                border: '1px solid #d2d2d7',
                fontSize: '16px',
                outline: 'none',
                backgroundColor: '#f5f5f7'
              }}
            />
            <Button 
              icon 
              circular 
              primary 
              disabled={inputText.trim() === ""}
              onClick={() => handleClickSend(inputText)}
              style={{ width: '46px', height: '46px', margin: 0 }}
            >
              <Icon name='arrow up' />
            </Button>
          </div>
        </Container>
      </div>

      <Modal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        size="small"
      >
        <Header icon="magic" content="평가 세션 생성 확인" />
        <Modal.Content>
          {chatSession && (
            <>
              <p>다음 채팅 세션에 대해 새로운 평가를 생성하시겠습니까?</p>
              <Table definition>
                <Table.Body>
                  <Table.Row>
                    <Table.Cell width={4}>ID</Table.Cell>
                    <Table.Cell>
                      {chatSession.id.slice(0, 8)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>일시</Table.Cell>
                    <Table.Cell>
                      {formatDate(chatSession.created_at)}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>교육생</Table.Cell>
                    <Table.Cell>
                      {chatSession.trainee?.full_name ||
                        chatSession.trainee?.username}
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>페르소나</Table.Cell>
                    <Table.Cell>{chatSession.persona?.name}</Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>기존 평가 수</Table.Cell>
                    <Table.Cell>
                      {chatSession.evaluation_session_count}회
                    </Table.Cell>
                  </Table.Row>
                </Table.Body>
              </Table>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" onClick={() => setConfirmModalOpen(false)}>
            <Icon name="remove" /> 취소
          </Button>
          <Button color="green" onClick={handleConfirmEvaluation}>
            <Icon name="checkmark" /> 생성
          </Button>
        </Modal.Actions>
      </Modal>

      <CustomSnackbar
        visible={snackbarVisible}
        title="권한 경고"
        message="다른 사용자의 세션을 이어할 수 없습니다."
        onClose={() => setSnackbarVisible(false)}
        type="warning"
      />
    </div>
  );

  return content;
};

export default forwardRef(ChatSessionPerform);
